import React, { useState, useEffect, useRef } from 'react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../../config/firebase';
import './Settings.css';

/* =========================================================
   PROFILE CHARACTER AVATARS
   ========================================================= */

const AVATARS = [
  { id: 'panda', emoji: '🐼', name: 'Panda' },
  { id: 'fox', emoji: '🦊', name: 'Fox' },
  { id: 'cat', emoji: '🐱', name: 'Cat' },
  { id: 'dog', emoji: '🐶', name: 'Dog' },
  { id: 'koala', emoji: '🐨', name: 'Koala' },

  { id: 'tiger', emoji: '🐯', name: 'Tiger' },
  { id: 'lion', emoji: '🦁', name: 'Lion' },
  { id: 'rabbit', emoji: '🐰', name: 'Rabbit' },
  { id: 'bear', emoji: '🐻', name: 'Bear' },
  { id: 'frog', emoji: '🐸', name: 'Frog' },

  { id: 'penguin', emoji: '🐧', name: 'Penguin' },
  { id: 'monkey', emoji: '🐵', name: 'Monkey' },
  { id: 'owl', emoji: '🦉', name: 'Owl' },
  { id: 'unicorn', emoji: '🦄', name: 'Unicorn' },
  { id: 'dragon', emoji: '🐉', name: 'Dragon' },

  { id: 'dolphin', emoji: '🐬', name: 'Dolphin' },
  { id: 'whale', emoji: '🐳', name: 'Whale' },
  { id: 'octopus', emoji: '🐙', name: 'Octopus' },
  { id: 'bee', emoji: '🐝', name: 'Bee' },
  { id: 'butterfly', emoji: '🦋', name: 'Butterfly' },

  { id: 'hamster', emoji: '🐹', name: 'Hamster' },
  { id: 'chick', emoji: '🐥', name: 'Chick' },
  { id: 'hedgehog', emoji: '🦔', name: 'Hedgehog' },
  { id: 'pug', emoji: '🐕', name: 'Pug' },
  { id: 'parrot', emoji: '🦜', name: 'Parrot' }
];

const DEFAULT_AVATAR = 'panda';

const Settings = () => {
  const bgCanvasRef = useRef(null);
  const cursorGlowRef = useRef(null);

  /* =========================================================
     AUTHENTICATED USER
     ========================================================= */

  const [user, setUser] = useState(auth.currentUser);

  /* =========================================================
     PROFILE STATE
     ========================================================= */

  const [profileName, setProfileName] = useState(
    auth.currentUser?.displayName || ''
  );

  const [selectedAvatar, setSelectedAvatar] = useState(
    localStorage.getItem('conscia_avatar') || DEFAULT_AVATAR
  );

  const [profileStatus, setProfileStatus] = useState({
    type: '',
    message: ''
  });

  const [isSavingProfile, setIsSavingProfile] = useState(false);

  /* =========================================================
     PASSWORD STATE
     ========================================================= */

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [passwordStatus, setPasswordStatus] = useState({
    type: '',
    message: ''
  });

  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  /* =========================================================
     FORGOT PASSWORD STATE
     ========================================================= */

  const [resetStatus, setResetStatus] = useState({
    type: '',
    message: ''
  });

  const [isSendingReset, setIsSendingReset] = useState(false);

  /* =========================================================
     LOAD CURRENT USER
     ========================================================= */

  useEffect(() => {
    const currentUser = auth.currentUser;

    if (currentUser) {
      setUser(currentUser);
      setProfileName(currentUser.displayName || '');
    }
  }, []);

  /* =========================================================
     BACKGROUND ANIMATION
     Disabled on mobile/tablet for performance
     ========================================================= */

  useEffect(() => {
    const isTouchDevice =
      window.matchMedia('(max-width: 768px)').matches ||
      window.matchMedia('(pointer: coarse)').matches;

    if (isTouchDevice) {
      if (cursorGlowRef.current) {
        cursorGlowRef.current.style.display = 'none';
      }

      return;
    }

    const handleMouseMove = (e) => {
      if (cursorGlowRef.current) {
        cursorGlowRef.current.style.left = `${e.clientX}px`;
        cursorGlowRef.current.style.top = `${e.clientY}px`;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);

    const canvas = bgCanvasRef.current;

    let animationId = null;
    let resizeHandler = null;

    if (canvas) {
      const ctx = canvas.getContext('2d');

      let w = 0;
      let h = 0;
      let particles = [];

      const resize = () => {
        w = canvas.width = window.innerWidth;
        h = canvas.height = window.innerHeight;
      };

      const createParticles = () => {
        particles = [];

        const count = Math.min(
          80,
          Math.floor((w * h) / 22000)
        );

        for (let i = 0; i < count; i++) {
          particles.push({
            x: Math.random() * w,
            y: Math.random() * h,
            r: Math.random() * 1.5 + 0.3,
            vx: (Math.random() - 0.5) * 0.15,
            vy: (Math.random() - 0.5) * 0.15,
            a: Math.random() * 0.5 + 0.1,
            color:
              Math.random() > 0.5
                ? '110,231,247'
                : '167,139,250'
          });
        }
      };

      const draw = () => {
        ctx.clearRect(0, 0, w, h);

        particles.forEach((p) => {
          p.x += p.vx;
          p.y += p.vy;

          if (p.x < 0) p.x = w;
          if (p.x > w) p.x = 0;
          if (p.y < 0) p.y = h;
          if (p.y > h) p.y = 0;

          ctx.beginPath();
          ctx.arc(
            p.x,
            p.y,
            p.r,
            0,
            Math.PI * 2
          );

          ctx.fillStyle =
            `rgba(${p.color},${p.a})`;

          ctx.fill();
        });

        animationId = requestAnimationFrame(draw);
      };

      resize();
      createParticles();
      draw();

      resizeHandler = () => {
        resize();
        createParticles();
      };

      window.addEventListener(
        'resize',
        resizeHandler
      );
    }

    return () => {
      document.removeEventListener(
        'mousemove',
        handleMouseMove
      );

      if (resizeHandler) {
        window.removeEventListener(
          'resize',
          resizeHandler
        );
      }

      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  /* =========================================================
     SAVE PROFILE
     ========================================================= */

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    setProfileStatus({
      type: '',
      message: ''
    });

    const name = profileName.trim();

    if (!name) {
      setProfileStatus({
        type: 'error',
        message: 'Please enter a profile name.'
      });

      return;
    }

    if (name.length < 2) {
      setProfileStatus({
        type: 'error',
        message:
          'Profile name must be at least 2 characters.'
      });

      return;
    }

    const currentUser = auth.currentUser;

    if (!currentUser) {
      setProfileStatus({
        type: 'error',
        message:
          'No authenticated user found.'
      });

      return;
    }

    setIsSavingProfile(true);

    try {
      await updateProfile(currentUser, {
        displayName: name
      });

      localStorage.setItem(
        'conscia_avatar',
        selectedAvatar
      );

      setUser(currentUser);

      setProfileStatus({
        type: 'success',
        message:
          'Your Conscia profile has been updated!'
      });

    } catch (error) {
      console.error(
        'Profile update error:',
        error
      );

      setProfileStatus({
        type: 'error',
        message:
          'Unable to update your profile. Please try again.'
      });

    } finally {
      setIsSavingProfile(false);
    }
  };

  /* =========================================================
     UPDATE PASSWORD
     ========================================================= */

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    setPasswordStatus({
      type: '',
      message: ''
    });

    if (!currentPassword) {
      setPasswordStatus({
        type: 'error',
        message:
          'Please enter your current password.'
      });

      return;
    }

    if (!newPassword) {
      setPasswordStatus({
        type: 'error',
        message:
          'Please enter a new password.'
      });

      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordStatus({
        type: 'error',
        message:
          'New passwords do not match.'
      });

      return;
    }

    if (newPassword.length < 6) {
      setPasswordStatus({
        type: 'error',
        message:
          'Password must be at least 6 characters long.'
      });

      return;
    }

    const currentUser = auth.currentUser;

    if (!currentUser || !currentUser.email) {
      setPasswordStatus({
        type: 'error',
        message:
          'No authenticated user found.'
      });

      return;
    }

    setIsUpdatingPassword(true);

    try {
      const credential =
        EmailAuthProvider.credential(
          currentUser.email,
          currentPassword
        );

      await reauthenticateWithCredential(
        currentUser,
        credential
      );

      await updatePassword(
        currentUser,
        newPassword
      );

      setPasswordStatus({
        type: 'success',
        message:
          'Password successfully updated. Your account is secure.'
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

    } catch (error) {
      console.error(
        'Password update error:',
        error
      );

      if (
        error.code === 'auth/wrong-password' ||
        error.code === 'auth/invalid-credential'
      ) {
        setPasswordStatus({
          type: 'error',
          message:
            'Incorrect current password.'
        });

      } else if (
        error.code === 'auth/too-many-requests'
      ) {
        setPasswordStatus({
          type: 'error',
          message:
            'Too many failed attempts. Try again later.'
        });

      } else {
        setPasswordStatus({
          type: 'error',
          message:
            'Failed to update password. Please try again.'
        });
      }

    } finally {
      setIsUpdatingPassword(false);
    }
  };

  /* =========================================================
     FORGOT PASSWORD
     ========================================================= */

  const handleForgotPassword = async () => {
    setResetStatus({
      type: '',
      message: ''
    });

    const email = auth.currentUser?.email;

    if (!email) {
      setResetStatus({
        type: 'error',
        message:
          'We could not find your account email.'
      });

      return;
    }

    setIsSendingReset(true);

    try {
      await sendPasswordResetEmail(
        auth,
        email
      );

      setResetStatus({
        type: 'success',
        message:
          `Password reset instructions were sent to ${email}.`
      });

    } catch (error) {
      console.error(
        'Password reset error:',
        error
      );

      if (
        error.code === 'auth/too-many-requests'
      ) {
        setResetStatus({
          type: 'error',
          message:
            'Too many reset requests. Please try again later.'
        });
      } else {
        setResetStatus({
          type: 'error',
          message:
            'Unable to send the reset email. Please try again.'
        });
      }

    } finally {
      setIsSendingReset(false);
    }
  };

  /* =========================================================
     CURRENT AVATAR
     ========================================================= */

  const activeAvatar =
    AVATARS.find(
      (avatar) =>
        avatar.id === selectedAvatar
    ) || AVATARS[0];

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div id="app">

      {/* Background */}

      <canvas
        id="bg-canvas"
        ref={bgCanvasRef}
      />

      <div
        id="cursor-glow"
        ref={cursorGlowRef}
      />

      <div className="settings-container">

        {/* ===================================================
            PAGE HEADER
        =================================================== */}

        <div className="settings-header">

          <div className="settings-header-icon">
            ⚙
          </div>

          <div className="settings-header-text">

            <h1 className="settings-title">
              Account Settings
            </h1>

            <p className="settings-subtitle">
              Make your Conscia profile yours.
            </p>

          </div>

        </div>

        {/* ===================================================
            PROFILE CARD
        =================================================== */}

        <div
          className="settings-card profile-card"
          style={{ '--card-i': 1 }}
        >

          <div className="settings-card-header">

            <div>
              <h2>Your Conscia Profile</h2>

              <p>
                Customize your profile and choose
                your favorite character.
              </p>
            </div>

          </div>

          {/* Profile status */}

          {profileStatus.message && (
            <div
              className={`settings-alert ${profileStatus.type}`}
            >
              <span className="settings-alert-icon">
                {profileStatus.type === 'success'
                  ? '✓'
                  : '⚠'}
              </span>

              <span>
                {profileStatus.message}
              </span>
            </div>
          )}

          <form
            onSubmit={handleProfileUpdate}
            className="profile-form"
          >

            {/* Current avatar */}

            <div className="profile-avatar-area">

              <div className="profile-avatar">

                <span>
                  {activeAvatar.emoji}
                </span>

              </div>

              <div className="avatar-info">

                <div className="avatar-title">
                  Profile character
                </div>

                <div className="avatar-subtitle">
                  Choose a character that represents you.
                </div>

              </div>

            </div>

            {/* Character picker */}

            <div>

              <label className="avatar-picker-label">
                Choose your character
              </label>

              <div className="avatar-picker">

                {AVATARS.map((avatar) => (
                  <button
                    key={avatar.id}
                    type="button"
                    className={`avatar-option ${
                      selectedAvatar === avatar.id
                        ? 'selected'
                        : ''
                    }`}
                    onClick={() =>
                      setSelectedAvatar(
                        avatar.id
                      )
                    }
                    aria-label={
                      `Choose ${avatar.name}`
                    }
                    title={avatar.name}
                  >
                    <span>
                      {avatar.emoji}
                    </span>
                  </button>
                ))}

              </div>

            </div>

            {/* Profile name */}

            <div className="form-field">

              <label htmlFor="profile-name">
                Profile Name
              </label>

              <div className="input-wrapper">

                <input
                  id="profile-name"
                  type="text"
                  placeholder="Enter your profile name"
                  value={profileName}
                  maxLength={40}
                  autoComplete="name"
                  onChange={(e) =>
                    setProfileName(
                      e.target.value
                    )
                  }
                  required
                />

                <div className="input-underline" />

              </div>

            </div>

            {/* Email */}

            <div className="form-field">

              <label htmlFor="profile-email">
                Email Address
              </label>

              <div className="profile-email-box">

                <span
                  className="email-icon"
                  aria-hidden="true"
                >
                  @
                </span>

                <input
                  id="profile-email"
                  type="email"
                  value={
                    user?.email || ''
                  }
                  readOnly
                  aria-readonly="true"
                />

                <span className="verified-badge">
                  Verified
                </span>

              </div>

              <small className="field-help">
                Your sign-in email is managed by
                Firebase and cannot be changed here.
              </small>

            </div>

            {/* Save */}

            <button
              type="submit"
              className="settings-btn btn-profile"
              disabled={isSavingProfile}
            >
              {isSavingProfile ? (
                <div className="btn-spinner" />
              ) : (
                <span>
                  Save Profile
                </span>
              )}
            </button>

          </form>

        </div>

        {/* ===================================================
            SECURITY CARD
        =================================================== */}

        <div
          className="settings-card card-password"
          style={{ '--card-i': 2 }}
        >

          <div className="settings-card-header">

            <div>

              <h2>Security</h2>

              <p>
                Keep your Conscia account protected.
              </p>

            </div>

            <div className="security-icon">
              🔐
            </div>

          </div>

          {/* Password status */}

          {passwordStatus.message && (
            <div
              className={`settings-alert ${passwordStatus.type}`}
            >
              <span className="settings-alert-icon">
                {passwordStatus.type === 'success'
                  ? '✓'
                  : '⚠'}
              </span>

              <span>
                {passwordStatus.message}
              </span>
            </div>
          )}

          <form
            onSubmit={handlePasswordUpdate}
            className="settings-form"
          >

            {/* Current password */}

            <div className="form-field">

              <label htmlFor="current-password">
                Current Password
              </label>

              <div className="input-wrapper">

                <input
                  id="current-password"
                  type="password"
                  placeholder="Enter current password"
                  value={currentPassword}
                  autoComplete="current-password"
                  onChange={(e) =>
                    setCurrentPassword(
                      e.target.value
                    )
                  }
                  required
                />

                <div className="input-underline" />

              </div>

            </div>

            {/* New password */}

            <div className="form-field">

              <label htmlFor="new-password">
                New Password
              </label>

              <div className="input-wrapper">

                <input
                  id="new-password"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={newPassword}
                  autoComplete="new-password"
                  onChange={(e) =>
                    setNewPassword(
                      e.target.value
                    )
                  }
                  required
                />

                <div className="input-underline" />

              </div>

            </div>

            {/* Confirm password */}

            <div className="form-field">

              <label htmlFor="confirm-password">
                Confirm New Password
              </label>

              <div className="input-wrapper">

                <input
                  id="confirm-password"
                  type="password"
                  placeholder="Type new password again"
                  value={confirmPassword}
                  autoComplete="new-password"
                  onChange={(e) =>
                    setConfirmPassword(
                      e.target.value
                    )
                  }
                  required
                />

                <div className="input-underline" />

              </div>

            </div>

            {/* Update password */}

            <button
              type="submit"
              className="settings-btn btn-password"
              disabled={isUpdatingPassword}
            >
              {isUpdatingPassword ? (
                <div className="btn-spinner" />
              ) : (
                <span>
                  Update Password
                </span>
              )}
            </button>

          </form>

          {/* =================================================
              FORGOT PASSWORD
          ================================================= */}

          <div className="forgot-password-panel">

            <div className="forgot-password-content">

              <h3>
                Forgot your password?
              </h3>

              <p>
                We'll send a secure reset link to
                your registered email address.
              </p>

            </div>

            <button
              type="button"
              className="forgot-password-btn"
              onClick={handleForgotPassword}
              disabled={isSendingReset}
            >
              {isSendingReset
                ? 'Sending...'
                : 'Send Reset Email'}
            </button>

          </div>

          {/* Reset status */}

          {resetStatus.message && (
            <div
              className={`settings-alert ${resetStatus.type}`}
            >
              <span className="settings-alert-icon">
                {resetStatus.type === 'success'
                  ? '✓'
                  : '⚠'}
              </span>

              <span>
                {resetStatus.message}
              </span>
            </div>
          )}

        </div>

      </div>

    </div>
  );
};

export default Settings;